/**
 * Estimated work duration as a slider and a number field side by side (#3091).
 *
 * The slider and the arrows (keyboard ArrowUp/ArrowDown and the buttons in the
 * field) move on the workshop's duration grid (`workDurationStepMinutes`). A
 * typed value stays free — Finnish commas included — so "2,25" is still valid
 * with a 30 min step; the next arrow press snaps it back onto the grid.
 */

import { useEffect, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormHelperText, IconButton, InputAdornment, Slider, TextField, Tooltip } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { formatDuration, stepDurationHours } from '@/utils/duration';

/** A full workday; the slider grows past it when a longer value is set. */
const SLIDER_MIN_MAX_HOURS = 8;

function hoursToInput(hours: number): string {
  return String(hours);
}

/** Allows 0 (unknown duration) and Finnish commas; rejects negatives. */
function parseHoursInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return 0;
  const parsed = Number.parseFloat(trimmed.replace(',', '.'));
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

interface DurationHoursInputProps {
  value: number;
  /** Omitted → the controls are shown but cannot change the value. */
  onChange?: (hours: number) => void;
  stepMinutes: number;
  label: string;
  helperText?: string;
  warning?: boolean;
}

export function DurationHoursInput({
  value,
  onChange,
  stepMinutes,
  label,
  helperText,
  warning = false,
}: DurationHoursInputProps) {
  const { t } = useTranslation('workorders');
  const readOnly = !onChange;
  const [input, setInput] = useState(() => hoursToInput(value));
  const [focused, setFocused] = useState(false);

  // Follow outside changes (service picks, slider) unless the user is typing:
  // rewriting "1," to "1" mid-keystroke would eat the comma.
  useEffect(() => {
    if (focused) return;
    setInput(hoursToInput(value));
  }, [value, focused]);

  const stepHours = stepMinutes / 60;
  const sliderMax = Math.max(SLIDER_MIN_MAX_HOURS, Math.ceil(value / stepHours) * stepHours);

  const commit = (hours: number) => {
    setInput(hoursToInput(hours));
    onChange?.(hours);
  };

  const step = (direction: 1 | -1) => {
    if (readOnly) return;
    const current = parseHoursInput(input) ?? value;
    commit(stepDurationHours(current, stepMinutes, direction));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      step(event.key === 'ArrowUp' ? 1 : -1);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Slider
          value={Math.min(value, sliderMax)}
          min={0}
          max={sliderMax}
          step={stepHours}
          disabled={readOnly}
          onChange={(_e, next) => {
            if (typeof next === 'number') commit(next);
          }}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => formatDuration(v)}
          {...(warning ? { color: 'warning' as const } : {})}
          slotProps={{ input: { 'aria-label': label } }}
          sx={{ flex: 1, minWidth: 100 }}
        />
        <TextField
          type="text"
          size="small"
          label={label}
          value={input}
          onChange={(event) => {
            const raw = event.target.value;
            setInput(raw);
            const parsed = parseHoursInput(raw);
            if (parsed !== null) onChange?.(parsed);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          {...(warning ? { color: 'warning' as const } : {})}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end" sx={{ ml: 0.5 }}>
                  h
                  <Box sx={{ display: 'flex', flexDirection: 'column', ml: 0.5 }}>
                    <Tooltip title={t('create.durationIncrease')}>
                      {/* span: a disabled button fires no events for the Tooltip */}
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('create.durationIncrease')}
                          onClick={() => step(1)}
                          disabled={readOnly}
                          sx={{ p: 0 }}
                        >
                          <KeyboardArrowUpIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('create.durationDecrease')}>
                      {/* span: a disabled button fires no events for the Tooltip */}
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('create.durationDecrease')}
                          onClick={() => step(-1)}
                          disabled={readOnly || value <= 0}
                          sx={{ p: 0 }}
                        >
                          <KeyboardArrowDownIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </InputAdornment>
              ),
            },
            htmlInput: {
              inputMode: 'decimal',
              'aria-label': label,
              readOnly,
            },
          }}
          sx={{ width: 150, flexShrink: 0 }}
        />
      </Box>
      {helperText && (
        <FormHelperText {...(warning ? { sx: { color: 'warning.main' } } : {})}>{helperText}</FormHelperText>
      )}
    </Box>
  );
}
